'use client'

import { useEffect, useRef, useState } from 'react'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import ThemeToggle from './ThemeToggle'
import { headerLinks, services } from '@/data/navigation'

// Below this width the nav collapses into the toggle-driven panel (matches globals.css)
const MOBILE_MAX_WIDTH = 1024
const FOCUSABLE = 'a[href], button:not([disabled])'

const isMobile = () => window.innerWidth <= MOBILE_MAX_WIDTH
const isActive = (pathname, href) => pathname === href || pathname.startsWith(`${href}/`)

function Logo() {
    return (
        <div className="nav-logo-wrap">
            <Link href="/" className="logo nav-brand" aria-label="Mallard Technologies">
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img src="/assets/images/mallard_logo_long_white.svg" alt="Mallard Technologies" width={250} height={48} style={{ width: '180px', height: 'auto' }} className="dark-mode-logo" />
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img src="/assets/images/mallard_logo_long.svg" alt="Mallard Technologies" width={250} height={48} style={{ width: '180px', height: 'auto' }} className="color-logo" />
            </Link>
        </div>
    )
}

function ServicesPanel({ pathname, onNavigate }) {
    return (
        <div className="mn-mega" id="nav-panel-services">
            <div className="mn-mega__group">
                <ul className="mn-mega__list">
                    {services.map((link) => {
                        const active = isActive(pathname, link.href)
                        return (
                            <li key={link.href}>
                                <Link
                                    href={link.href}
                                    className={`mn-mega__link${active ? ' active' : ''}`}
                                    aria-current={active ? 'page' : undefined}
                                    onClick={onNavigate}
                                >
                                    <div className="mn-mega__link-body">
                                        <span className="mn-mega__link-text">{link.text}</span>
                                        <span className="mn-mega__link-desc">{link.description}</span>
                                    </div>
                                    <span className="mn-mega__arrow" aria-hidden="true">&rarr;</span>
                                </Link>
                            </li>
                        )
                    })}
                </ul>
                <div className="mn-mega__footer">
                    <Link href="/about" className="mn-mega__footer-link" onClick={onNavigate}>
                        About Mallard <span aria-hidden="true">&rarr;</span>
                    </Link>
                </div>
            </div>
        </div>
    )
}

/*
 * Fixed site header. Desktop: the Services dropdown opens on hover/focus via CSS
 * and on click via `open`. Phones: the toggle opens the panel, Tab is trapped
 * inside it and Escape closes it.
 */
export default function Header() {
    const pathname = usePathname()
    const [menuOpen, setMenuOpen] = useState(false)
    const [open, setOpen] = useState(false) // Services opened by click / keyboard
    const [hovered, setHovered] = useState(false) // mirrors CSS hover/focus-within for aria-expanded
    const [dismissed, setDismissed] = useState(false) // Escape beats hover until the pointer leaves
    const [scrolled, setScrolled] = useState(false)
    const toggleRef = useRef(null)
    const panelRef = useRef(null)
    const itemRef = useRef(null)
    const servicesRef = useRef(null)

    const closeAll = () => {
        setMenuOpen(false)
        setOpen(false)
        setHovered(false)
    }

    useEffect(() => {
        closeAll()
    }, [pathname])

    // Scroll lock while the phone menu is open
    useEffect(() => {
        document.documentElement.classList.toggle('mobile-menu-open', menuOpen)
        return () => document.documentElement.classList.remove('mobile-menu-open')
    }, [menuOpen])

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 10)
        onScroll()
        window.addEventListener('scroll', onScroll, { passive: true })
        return () => window.removeEventListener('scroll', onScroll)
    }, [])

    useEffect(() => {
        const onKeyDown = (event) => {
            if (event.key === 'Escape') {
                if (open || hovered) {
                    setOpen(false)
                    setHovered(false)
                    if (!isMobile()) {
                        setDismissed(true)
                    }
                    servicesRef.current?.focus()
                } else if (menuOpen) {
                    setMenuOpen(false)
                    toggleRef.current?.focus()
                }
                return
            }
            if (event.key !== 'Tab' || !menuOpen) {
                return
            }
            // Focus trap: the toggle plus everything visible in the panel
            const focusables = [
                toggleRef.current,
                ...Array.from(panelRef.current?.querySelectorAll(FOCUSABLE) ?? []).filter((el) => el.offsetParent !== null),
            ]
            const first = focusables[0]
            const last = focusables[focusables.length - 1]
            const inside = focusables.includes(document.activeElement)
            if (event.shiftKey && (document.activeElement === first || !inside)) {
                event.preventDefault()
                last.focus()
            } else if (!event.shiftKey && (document.activeElement === last || !inside)) {
                event.preventDefault()
                first.focus()
            }
        }
        const onClick = (event) => {
            if (!isMobile() && !itemRef.current?.contains(event.target)) {
                setOpen(false)
            }
        }
        const onResize = () => {
            if (!isMobile()) {
                setMenuOpen(false)
            }
        }
        window.addEventListener('keydown', onKeyDown)
        window.addEventListener('resize', onResize)
        document.addEventListener('click', onClick)
        return () => {
            window.removeEventListener('keydown', onKeyDown)
            window.removeEventListener('resize', onResize)
            document.removeEventListener('click', onClick)
        }
    }, [menuOpen, open, hovered])

    const toggleServices = () => {
        const willOpen = !open
        setOpen(willOpen)
        setHovered(false)
        setDismissed(!willOpen && !isMobile())
    }

    const hover = () => {
        if (!isMobile() && !dismissed) {
            setHovered(true)
        }
    }

    const leave = (event) => {
        // mouseleave has no relatedTarget inside; blur does
        if (itemRef.current?.contains(event.relatedTarget ?? document.activeElement)) {
            return
        }
        setDismissed(false)
        if (!isMobile()) {
            setOpen(false)
            setHovered(false)
        }
    }

    const servicesActive = services.some((link) => isActive(pathname, link.href))

    return (
        <header>
            <nav className={`main-nav${scrolled ? ' nav-scrolled' : ''}`} aria-label="Main">
                <div className="main-nav-sub">
                    <Logo />
                    <button
                        ref={toggleRef}
                        type="button"
                        onClick={() => setMenuOpen((value) => !value)}
                        className={`mobile-nav${menuOpen ? ' active' : ''}`}
                        aria-label="Toggle navigation menu"
                        aria-expanded={menuOpen}
                        aria-controls="site-mobile-menu"
                    >
                        <i className="mobile-nav-icon" aria-hidden="true" />
                    </button>
                    <div ref={panelRef} className={`inner-nav desktop-nav${menuOpen ? ' js-opened' : ''}`} id="site-mobile-menu">
                        <ul className="clearlist">
                            {headerLinks.map((link) => {
                                const active = isActive(pathname, link.href)
                                return (
                                    <li key={link.href}>
                                        <Link
                                            href={link.href}
                                            className={active ? 'active' : undefined}
                                            aria-current={active ? 'page' : undefined}
                                            onClick={closeAll}
                                        >
                                            {link.text}
                                        </Link>
                                    </li>
                                )
                            })}
                            <li
                                ref={itemRef}
                                data-nav-item
                                className={`${open ? 'js-opened' : ''}${dismissed ? ' nav-dismissed' : ''}`.trim() || undefined}
                                onMouseEnter={hover}
                                onFocus={hover}
                                onMouseLeave={leave}
                                onBlur={leave}
                            >
                                <button
                                    ref={servicesRef}
                                    type="button"
                                    className={`mn-has-sub${servicesActive ? ' active' : ''}`}
                                    aria-expanded={open || hovered}
                                    aria-controls="nav-panel-services"
                                    onClick={toggleServices}
                                >
                                    Services
                                    <span className="mn-has-sub__icon" aria-hidden="true">+</span>
                                </button>
                                <ServicesPanel pathname={pathname} onNavigate={closeAll} />
                            </li>
                        </ul>
                        <ul className="items-end clearlist">
                            <li>
                                <ThemeToggle />
                            </li>
                            <li>
                                <Link href="/contact" className="nav-cta-link" onClick={closeAll}>
                                    Contact us
                                </Link>
                            </li>
                        </ul>
                    </div>
                </div>
            </nav>
        </header>
    )
}
